import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { generatePageNumbers } from "utils/generatePageNumbers";

const CustomTable = ({
  columns,
  data,
  currentPage,
  totalPages,
  onPageChange,
  isLoading,
  rowSize = 10,
  minWidth = "auto",
}) => {
  const { t } = useTranslation("tables");
  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: { pageIndex: currentPage - 1 },
        manualPagination: true,
        pageCount: totalPages,
      },
      useSortBy,
      usePagination
    );

  const handlePageClick = (e, page) => {
    e.preventDefault();
    if (page >= 1 && page <= totalPages && !isLoading) {
      onPageChange(page);
    }
  };

  const [isDarkMode, setIsDarkMode] = useState(
    document.body.classList.contains("white-content")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("white-content"));
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [isDarkMode]);

  const { key: tableKey, ...tableProps } = getTableProps();
  const { key: tbodyKey, ...tbodyProps } = getTableBodyProps();

  return (
    <>
      <div className="table-responsive">
        <table
          key={tableKey}
          {...tableProps}
          className="table table-striped"
          style={{ width: "100%", minWidth: minWidth, tableLayout: "fixed" }}
        >
          <thead>
            {headerGroups?.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup?.headers?.map((column) => {
                    const sortByProps = column.getSortByToggleProps
                      ? column.getSortByToggleProps()
                      : {};
                    const { key: sortKey, ...restSortProps } = sortByProps;
                    const { key: columnKey, ...headerProps } =
                      column.getHeaderProps(restSortProps);

                    return (
                      <th
                        key={columnKey}
                        {...headerProps}
                        style={{
                          inlineSize: column.width || "auto",
                        }}
                      >
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody key={tbodyKey} {...tbodyProps}>
            {isLoading ? (
              <SkeletonTheme
                baseColor={isDarkMode ? "#ebebeb" : "#2c2c3a"}
                highlightColor={isDarkMode ? "#f5f5f5" : "#3a3a4a"}
              >
                {Array.from({ length: rowSize }).map((_, rowIndex) => (
                  <tr key={`skeleton-row-${rowIndex}`}>
                    {columns.map((_, colIndex) => (
                      <td key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                        <Skeleton />
                      </td>
                    ))}
                  </tr>
                ))}
              </SkeletonTheme>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  {t("noRecords")}
                </td>
              </tr>
            ) : (
              page.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td key={cellKey} {...cellProps}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <nav
          className="d-flex justify-content-end"
          aria-label={t("pagination")}
        >
          <Pagination listClassName="mb-0">
            <PaginationItem disabled={isLoading || currentPage === 1}>
              <PaginationLink
                onClick={(e) => handlePageClick(e, currentPage - 1)}
              >
                <span aria-hidden={true}>
                  <i className="tim-icons icon-double-left" />
                </span>
              </PaginationLink>
            </PaginationItem>
            {generatePageNumbers(currentPage, totalPages).map((page, index) => (
              <PaginationItem
                key={`page-${index}`}
                disabled={isLoading || page === "..."}
                active={currentPage === page}
              >
                <PaginationLink
                  onClick={(e) => page !== "..." && handlePageClick(e, page)}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem disabled={isLoading || currentPage === totalPages}>
              <PaginationLink
                onClick={(e) => handlePageClick(e, currentPage + 1)}
              >
                <span aria-hidden={true}>
                  <i className="tim-icons icon-double-right" />
                </span>
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </nav>
      )}
    </>
  );
};

export default CustomTable;
